import type {
    TcrCampaignStatus,
    TcrEntityType,
    TcrSource,
    TcrVendor,
    TcrVettingClass,
    TcrVettingStatus,
} from '~/services/adminTenDlcClient.types';
import { humanize } from '~/utils/string';

export const sourceLabels: Record<TcrSource, string> = {
    tcr: 'TCR',
    twilio: 'Twilio',
};

export const vendorLabels: Record<TcrVendor, string> = {
    bandwidth: 'Bandwidth',
    twilio: 'Twilio',
};

export const entityTypeLabels: Record<TcrEntityType, string> = {
    government: 'Government',
    non_profit: 'Non-Profit',
    private_profit: 'Private Company',
    public_profit: 'Publicly Traded Company',
    sole_proprietor: 'Sole Proprietor',
};

export const vettingClassLabels: Record<TcrVettingClass, string> = {
    authplus: 'AuthPlus',
    enhanced: 'Enhanced',
    political: 'Political',
    rbm: 'RBM',
    standard: 'Standard',
};

export const vettingStatusLabels: Record<TcrVettingStatus, string> = {
    active: 'Active',
    expired: 'Expired',
    failed: 'Failed',
    pending: 'Pending',
    unscored: 'Unscored',
};

export const campaignStatusLabels: Record<TcrCampaignStatus, string> = {
    active: 'Active',
    expired: 'Expired',
    failed: 'Failed',
    pending: 'Pending',
};

// Curated names for the TCR use case ids whose display name is not plain
// title case; unknown ids fall back to title-cased humanize().
const useCaseLabels: Record<string, string> = {
    '2fa': '2FA',
    k12_education: 'K-12 Education',
    m2m: 'M2M',
    polling_voting: 'Polling & Voting',
    ucaas_high: 'UCaaS High Volume',
    ucaas_low: 'UCaaS Low Volume',
};

export const useCaseLabel = (usecase: string): string => useCaseLabels[usecase] ?? humanize(usecase);

export const messageClassLabel = (messageClass: string): string => `AT&T Class ${messageClass}`;

export const verifiedLabel = (verified: boolean | null): string => (true === verified ? 'Verified' : 'Unverified');

export const verificationLabel = (verified: boolean | null, vettingScore: number | null): string => {
    if (true !== verified || null === vettingScore) {
        return verifiedLabel(verified);
    }

    return `Verified · ${vettingScore}`;
};

// AT&T 10DLC per-campaign throughput tiers by message class.
const messageClassDetails: Record<string, string> = {
    A: '4,500 SMS/min toward AT&T · top vetting tier (score 75–100), dedicated use case',
    B: '4,500 SMS/min toward AT&T · top vetting tier (score 75–100), mixed/marketing',
    C: '2,400 SMS/min toward AT&T · mid vetting tier (score 50–74), dedicated use case',
    D: '2,400 SMS/min toward AT&T · mid vetting tier (score 50–74), mixed/marketing',
    E: '240 SMS/min toward AT&T · low vetting tier (score 1–49), dedicated use case',
    F: '240 SMS/min toward AT&T · low vetting tier (score 1–49), mixed/marketing',
    G: '60 SMS/min toward AT&T · group messaging',
    K: '4,500 SMS/min toward AT&T · election',
    N: '60 SMS/min toward AT&T · agents/local',
    P: '2,400 SMS/min toward AT&T · charity',
    S: '9,000 SMS/min toward AT&T · social — Voxie paces it at 4,500',
    T: '75 SMS/min toward AT&T · low-volume mixed campaigns',
    W: '15 SMS/min toward AT&T · sole proprietor',
    X: '4,500 SMS/min toward AT&T · emergency',
    Y: '3,000 SMS/min toward AT&T · trusted exemption',
    Z: '6 SMS/min toward AT&T · trial',
};

export const messageClassDetail = (messageClass: string): string | null => messageClassDetails[messageClass] ?? null;
